/**
 * GoogleTileSession — wraps Google Maps Tiles API session-token negotiation.
 *
 * Google Maps Tiles API requires a 2-step flow for raster tiles:
 *   1. POST /v1/createSession with API key + mapType → returns session token + expiry
 *   2. GET  /v1/2dtiles/{z}/{x}/{y}?session=...&key=...  → fetches tiles
 *
 * Sessions are valid for up to 2 weeks but we refresh proactively when within
 * 1 hour of expiry. The token is cached in localStorage so reloads don't burn
 * a fresh session quota every time.
 *
 * Reference: https://developers.google.com/maps/documentation/tile/2d-tiles-overview
 */

const STORAGE_KEY = "beacon-golf:google-tile-session-v1";
const REFRESH_BUFFER_MS = 60 * 60 * 1000; // refresh if <1h until expiry
const CREATE_SESSION_URL = "https://tile.googleapis.com/v1/createSession";
const TILE_URL_BASE = "https://tile.googleapis.com/v1/2dtiles";

export class GoogleTileSession {
  /**
   * @param {Object} opts
   * @param {string} opts.apiKey
   * @param {string} [opts.mapType="satellite"]  - satellite | roadmap | terrain | streetview
   * @param {string} [opts.language="en-US"]
   * @param {string} [opts.region="US"]
   */
  constructor({ apiKey, mapType = "satellite", language = "en-US", region = "US" }) {
    if (!apiKey) throw new Error("GoogleTileSession: apiKey required");
    this.apiKey = apiKey;
    this.mapType = mapType;
    this.language = language;
    this.region = region;

    this.session = null; // string token
    this.expiryMs = 0;
    this.tileSize = 256;
    this.imageFormat = "jpeg";

    this._loadCached();
  }

  /** Hydrate from localStorage if present and unexpired. */
  _loadCached() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const obj = JSON.parse(raw);
      // Validate shape + key fingerprint (so swapping API keys invalidates the cache)
      if (!obj || obj.keyFingerprint !== this._keyFingerprint() || obj.mapType !== this.mapType) return;
      if (obj.expiryMs - Date.now() < REFRESH_BUFFER_MS) return; // too close to expiry
      this.session = obj.session;
      this.expiryMs = obj.expiryMs;
      this.tileSize = obj.tileSize || 256;
      this.imageFormat = obj.imageFormat || "jpeg";
      console.info(
        `[GoogleTileSession] cache hit, expires in ${Math.round(
          (this.expiryMs - Date.now()) / 60000
        )}min`
      );
    } catch (err) {
      console.warn("[GoogleTileSession] cache read failed:", err);
    }
  }

  /** Persist to localStorage. */
  _persist() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          keyFingerprint: this._keyFingerprint(),
          mapType: this.mapType,
          session: this.session,
          expiryMs: this.expiryMs,
          tileSize: this.tileSize,
          imageFormat: this.imageFormat,
        })
      );
    } catch (err) {
      console.warn("[GoogleTileSession] cache write failed:", err);
    }
  }

  /** Don't store the full key, just a fingerprint sufficient to invalidate cache on swap. */
  _keyFingerprint() {
    if (!this.apiKey) return "";
    return `${this.apiKey.slice(0, 6)}…${this.apiKey.slice(-4)}`;
  }

  /** True if we have a valid session well within its expiry. */
  isValid() {
    return Boolean(this.session && this.expiryMs - Date.now() > REFRESH_BUFFER_MS);
  }

  /**
   * Ensure a valid session, fetching a new one if needed.
   * @returns {Promise<string>} the session token
   */
  async ensure() {
    if (this.isValid()) return this.session;
    return this.refresh();
  }

  async refresh() {
    const url = `${CREATE_SESSION_URL}?key=${encodeURIComponent(this.apiKey)}`;
    const body = {
      mapType: this.mapType,
      language: this.language,
      region: this.region,
    };

    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!resp.ok) {
      const errBody = await resp.text().catch(() => "");
      throw new Error(`createSession ${resp.status}: ${errBody.slice(0, 200)}`);
    }

    const data = await resp.json();
    if (!data.session) throw new Error("createSession returned no session token");

    this.session = data.session;
    // expiry is a Unix timestamp string in seconds per Google docs
    this.expiryMs = Number(data.expiry) * 1000;
    if (!Number.isFinite(this.expiryMs) || this.expiryMs < Date.now()) {
      // Defensive: if Google returns a malformed expiry, default to 1 hour
      console.warn("[GoogleTileSession] suspicious expiry, defaulting to +1h:", data.expiry);
      this.expiryMs = Date.now() + 60 * 60 * 1000;
    }
    this.tileSize = Number(data.tileWidth) || 256;
    this.imageFormat = data.imageFormat || "jpeg";

    this._persist();
    console.info(
      `[GoogleTileSession] new session, expires in ${Math.round(
        (this.expiryMs - Date.now()) / 60000
      )}min, tileSize=${this.tileSize}`
    );
    return this.session;
  }

  /**
   * Build the mapbox-gl-compatible raster source URL template.
   * @returns {string} URL with {z}/{x}/{y} placeholders for mapbox-gl
   */
  tileUrlTemplate() {
    if (!this.session) throw new Error("GoogleTileSession.tileUrlTemplate: no session — call ensure() first");
    return `${TILE_URL_BASE}/{z}/{x}/{y}?session=${encodeURIComponent(
      this.session
    )}&key=${encodeURIComponent(this.apiKey)}`;
  }

  /** Drop the cache. Used when source repeatedly errors. */
  invalidate() {
    this.session = null;
    this.expiryMs = 0;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }
}
